import React from "react";
import { cn } from "../../DashboardPage/lib/utils";
import { Skeleton } from "./skeleton";

export interface StatCardProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Value still loading: shows a placeholder bar instead of "—" or a stale 0. */
  loading?: boolean;
  /** Rendered inside a tinted circular badge on the leading edge. */
  icon?: React.ReactNode;
  /**
   * Overrides the icon badge's tone. Only use where the colour carries real
   * meaning (e.g. API Tokens' active/expired counts) — never to give an
   * otherwise-uniform row of cards a decorative spread of colours.
   */
  iconClassName?: string;
  /**
   * Optional comparison pill on the trailing edge (e.g. "↑ 12%"). Only pass this
   * when the underlying data actually supports a comparison — never a
   * decorative or invented trend.
   */
  trend?: {
    value: React.ReactNode;
  /** Value still loading: shows a placeholder bar instead of "—" or a stale 0. */
  loading?: boolean;
    direction?: TrendDirection;
  };
  className?: string;
}

type TrendDirection = "up" | "down" | "neutral";

const trendToneClass = (direction: TrendDirection) => {
  switch (direction) {
    case "up":
      return "bg-success-50 text-success-700 border-success-200";
    case "down":
      return "bg-danger-50 text-danger-700 border-danger-200";
    default:
      return "bg-neutral-100 text-neutral-600 border-neutral-200";
  }
};

/**
 * Compact metric tile for a stat summary row: a tinted icon badge on the
 * leading edge, a muted label above the value, and an optional trend pill on
 * the trailing edge.
 */
export default function StatCard({
  label,
  value,
  loading,
  icon,
  iconClassName,
  trend,
  className,
}: StatCardProps) {
  return (
    <div
      className={cn(
        // Horizontal layout: badge and text share a row rather than stacking,
        // which keeps the summary row compact above the table.
        "flex items-center gap-3 px-3.5 py-3 bg-white border border-neutral-100 rounded-xl",
        className
      )}
    >
      {icon && (
        <span
          className={cn(
            "inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0",
            iconClassName ?? "bg-primary-50 text-primary-700"
          )}
        >
          {icon}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-neutral-500 truncate">
          {label}
        </div>
        <div className="text-xl font-bold leading-tight text-black truncate">
          {loading ? (
            // Same line height as the value, so the card doesn't resize on load.
            <Skeleton className="w-12 h-6 my-0.5" />
          ) : (
            value
          )}
        </div>
      </div>
      {trend && (
        <span
          className={cn(
            "inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full shrink-0",
            trendToneClass(trend.direction ?? "neutral")
          )}
        >
          {trend.value}
        </span>
      )}
    </div>
  );
}
